/**
 * Utilitários de formatação/parse de tempos de transcrição.
 *
 * O backend trabalha com `start`/`end` em segundos (float, com milissegundos),
 * mas o usuário deve enxergar e editar em hh:mm:ss.
 */

/**
 * Converte segundos (float) para `hh:mm:ss` (descarta milissegundos).
 * Sempre devolve hh com pelo menos 2 dígitos.
 *
 * @param {number} seconds
 * @returns {string}
 */
export function formatHHMMSS(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return "00:00:00";
  const total = Math.max(0, Math.floor(Number(seconds)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/**
 * Converte uma string `hh:mm:ss` (ou `mm:ss`, ou `ss`) para segundos (inteiro).
 * Devolve `null` se o input for inválido.
 *
 * @param {string} value
 * @returns {number|null}
 */
export function parseHHMMSS(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const parts = trimmed.split(":").map((p) => p.trim());
  if (parts.some((p) => p === "" || /[^0-9]/.test(p))) return null;
  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n) || n < 0)) return null;

  let h = 0;
  let m = 0;
  let s = 0;
  if (nums.length === 1) {
    [s] = nums;
  } else if (nums.length === 2) {
    [m, s] = nums;
  } else if (nums.length === 3) {
    [h, m, s] = nums;
  } else {
    return null;
  }

  if (m >= 60 || s >= 60) return null;
  return h * 3600 + m * 60 + s;
}

/**
 * Decompõe uma duração em segundos em horas/minutos/segundos. Útil para
 * formatar a duração em palavras (ex.: "1 hora, 23 minutos e 51 segundos").
 *
 * @param {number} seconds
 * @returns {{ hours:number, minutes:number, seconds:number }}
 */
export function decomposeDuration(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) {
    return { hours: 0, minutes: 0, seconds: 0 };
  }
  const total = Math.max(0, Math.floor(Number(seconds)));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return { hours, minutes, seconds: secs };
}

/**
 * Devolve uma string descritiva da duração no formato "1 hora, 23 minutos e
 * 51 segundos" (com singular/plural correto e sem partes zeradas no início).
 *
 * Não usa o `i18next` para singular/plural porque o cálculo de qual termo
 * unir com "e" depende de quais partes estão presentes — fica mais legível
 * resolver tudo aqui com base nos rótulos passados pelo consumidor.
 *
 * @param {number} seconds
 * @param {{
 *   hour:string, hours:string,
 *   minute:string, minutes:string,
 *   second:string, seconds:string,
 *   conjunction:string,
 * }} labels
 * @returns {string}
 */
export function formatDurationWords(seconds, labels) {
  const { hours, minutes, seconds: secs } = decomposeDuration(seconds);
  const parts = [];
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? labels.hour : labels.hours}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? labels.minute : labels.minutes}`);
  }
  /* Mostramos o componente de segundos quando ele é > 0 OU quando ele é a
     única parte (ex.: 0s → "0 segundos"). Assim "0:01" vira "1 segundo" e
     "0:00" vira "0 segundos". */
  if (secs > 0 || parts.length === 0) {
    parts.push(`${secs} ${secs === 1 ? labels.second : labels.seconds}`);
  }
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} ${labels.conjunction} ${parts[1]}`;
  /* 3+ partes: "A, B e C" */
  const head = parts.slice(0, -1).join(", ");
  const tail = parts[parts.length - 1];
  return `${head} ${labels.conjunction} ${tail}`;
}

/**
 * Calcula o tempo "ideal" para a frase nascida de um split:
 * a metade do gap entre a frase atual (end ou start como fallback) e a próxima
 * (start), limitado a no máximo 5s após o ponto de origem.
 *
 * Robustez contra overlap: quando o `end` da frase atual já alcança ou passa
 * o `start` da próxima (dados originais com sobreposição), caímos para o
 * `start` da frase atual como origem, para que o resultado fique sempre dentro
 * de `(current.start, next.start)` e nunca crie uma frase fora de ordem.
 *
 * @param {{ start?: number, end?: number }} current  frase de origem
 * @param {{ start?: number }|null} next              frase imediatamente posterior (ou null)
 * @returns {number} tempo em segundos para o `start` da nova frase
 */
export function computeSplitStartTime(current, next) {
  const currentStart = Number.isFinite(current?.start) ? current.start : 0;
  const baseEnd = Number.isFinite(current?.end) ? current.end : currentStart;
  const nextStart =
    next && Number.isFinite(next.start) ? next.start : null;

  /* Se o `end` atual já invade o `start` da próxima, ele não serve como
     origem — usa-se o `start` atual. */
  const origin =
    nextStart != null && baseEnd >= nextStart ? currentStart : baseEnd;

  if (nextStart == null) {
    return origin + 5;
  }
  if (nextStart <= origin) {
    /* Dados patológicos (próxima sobrepõe até o start atual). Melhor esforço:
       ficar logo antes do início da próxima, sem nunca recuar do start atual. */
    return Math.max(currentStart, nextStart - 1);
  }
  const halfway = origin + (nextStart - origin) / 2;
  const fiveLater = origin + 5;
  return Math.min(halfway, fiveLater);
}
